import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import AdminNotificationPanel from "../../../components/AdminCentral/Dashboard/AdminNotificationPanel";
import SubscriptionAdminDashboardContent from "../../../components/AdminCentral/Dashboard/SubscriptionAdminDashboardContent";
import SubscriptionPOCDetailContent from "../../../components/AdminCentral/Dashboard/SubscriptionPOCDetailContent";
import AnnouncementCard from "../../../components/AdminCentral/Announcements/AnnouncmentCard";
import DashboardTabs from "../../../components/AdminCentral/Dashboard/DashboardTabs";
import SubscriptionsContent from "../../../components/AdminCentral/Dashboard/SubscriptionsContentsDashboard";

const breakpoints = {
  tablet: 768,
  phone:  480,
};

const ISAFE_ROLES = new Set([
  "isafe_accountant",
  "isafe_moderator",
  "isafe_salesperson",
  "isafe_admin",
  "root",
]);
const SUBSCRIPTION_ROLES = new Set([
  "subscription_access",
  "subscription_accountant",
  "subscription_admin",
  "subscription_implementor",
  "subscription_salesperson",
]);

const Page = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  min-height: 100vh;
  background-color: transparent;
`;

const Title = styled.h1`
  align-self: flex-start;
  width: 100%;
  max-width: 1100px;
  font-size: 32px;
  color: ${({ colors }) => colors.titleText};
  font-weight: 600;
  margin: 0 0 12px 0;
  text-align: left;

  @media (max-width: ${breakpoints.phone}px) {
    font-size: 28px;
    text-align: left;
  }
`;

const Container = styled.div`
  width: 1100px;
  max-width: 100%;
  padding: 20px;
  box-sizing: border-box;
  border-radius: 8px;
  background: ${({ colors }) => colors.cardBackground};
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);

  @media (max-width: ${breakpoints.tablet}px) {
    width: 90%;
    padding: 16px;
  }

  @media (max-width: ${breakpoints.phone}px) {
    width: 100%;
    padding: 14px;
  }
`;

const InnerCard = styled.div`
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ContentRow = styled.div`
  display: grid;
  grid-template-columns: 25% 1fr;
  gap: 12px;
  align-items: stretch;

  @media (max-width: ${breakpoints.tablet}px) {
    grid-template-columns: 30% 1fr;
  }

  @media (max-width: ${breakpoints.phone}px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const AnnouncementWrapper = styled.div`
  display: flex;
  flex-direction: column;

  > * {
    width: 100%;
  }

  @media (max-width: ${breakpoints.phone}px) {
    width: 100%;
  }
`;

const DetailsWrapper = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;

  > * {
    width: 100%;
  }

  @media (max-width: ${breakpoints.phone}px) {
    width: 100%;
  }
`;

const SectionTitle = styled.h2`
  margin: 0 0 12px;
  font-size: 20px;
  color: ${({ colors }) => colors.titleText};
`;

const DASHBOARD_TABS = ["Accounts", "Dashboard"];

export default function AdminCentralDashboardPage({ colors }) {
  const user = useSelector(state => state.auth.user);
  const become = useSelector(state => state.auth.become)
  const userRoles = useSelector((s) => s.auth.user.roles) || [];
  const persistedAccountsFlag = useSelector((s) => s.auth.show_accounts_listing);
  
  const [showPOCDetail, setShowPOCDetail] = useState(false);
  const [selectedTab, setSelectedTab] = useState("Dashboard");
  const customerAccounts =
    user && typeof user.customer_accounts === "object"
      ? user.customer_accounts
      : {};

  const derivedAccountsFlag = useMemo(() => {
    const hasMultiple =
      customerAccounts && Object.keys(customerAccounts).length > 1;
    const hasSubscriptionRole = userRoles.some((r) =>
      SUBSCRIPTION_ROLES.has(r.id)
    );
    return Boolean(hasMultiple && hasSubscriptionRole);
  }, [customerAccounts, userRoles]);

  const showAccountsListing = persistedAccountsFlag || derivedAccountsFlag;
  const allowedCustomerIds = useMemo(
    () =>
      Object.values(customerAccounts || {}).map(
        (entry) => entry.customer_id || entry
      ),
    [customerAccounts]
  );

  useEffect(() => {
    // Default to listing view for multi-sub accounts; otherwise show dashboard.
    setSelectedTab(showAccountsListing ? "Accounts" : "Dashboard");
  }, [showAccountsListing]);

    const isISAFEAdmin = userRoles.some((r) =>
    ISAFE_ROLES.has(r.id)
  );
  const isSubscriptionAdmin =
    !isISAFEAdmin && userRoles.some((r) =>
      SUBSCRIPTION_ROLES.has(r.id)
  );
  const showSubscriptionAdmin =
    become === 1 || isSubscriptionAdmin;
  const pageTitle = selectedTab === "Accounts" ? "Accounts" : "Dashboard";
  const tabs = showAccountsListing ? DASHBOARD_TABS : ["Dashboard"];
  return (
    <Page>
      <Title colors={colors}>{pageTitle}</Title>
      <Container colors={colors}>
        {showAccountsListing && (
          <DashboardTabs
            colors={colors}
            tabs={tabs}
            selectedTab={selectedTab}
            onTabClick={(tab) => {
              setSelectedTab(tab);
              if (tab === "Dashboard") setShowPOCDetail(false);
            }}
          />
        )}

        {showAccountsListing && selectedTab === "Accounts" && (
          <InnerCard>
            <div>
              <SubscriptionsContent
                colors={colors}
                allowedCustomerIds={allowedCustomerIds}
                dedupeByCustomerLatest
                onOrgSelect={() => setShowPOCDetail(false)}
              />
            </div>
          </InnerCard>
        )}

        {selectedTab === "Dashboard" && (
          <InnerCard>
            <ContentRow>
              <AnnouncementWrapper>
                <AnnouncementCard colors={colors} />
              </AnnouncementWrapper>
              <DetailsWrapper>
                {showSubscriptionAdmin ? (
                  showPOCDetail ? (
                    <SubscriptionPOCDetailContent
                      colors={colors}
                      onBack={() => setShowPOCDetail(false)}
                    />
                  ) : (
                    <SubscriptionAdminDashboardContent
                      colors={colors}
                      onViewMore={() => setShowPOCDetail(true)}
                    />
                  )
                ) : (
                  <AdminNotificationPanel colors={colors} />
                )}
              </DetailsWrapper>
            </ContentRow>
          </InnerCard>
        )}
      </Container>
    </Page>
  );
}
